import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { LiveBlock } from './live/LiveBlock'
import { SentEcho } from './live/SentEcho'
import { Msg, PeekButton, ToolRun, Turn } from './transcriptBlocks'
import { useSenderResolution } from './useSenderResolution'
import { TabFactsContext, lastTabTouched, tabFacts } from './browserActivity'
import { agentKey, noteLastTab, windowIsTheirs } from './lastTab'
import { useTranscriptWindow } from './useTranscriptWindow'
import { useAgentPulse } from './useAgentPulse'
import { type AgentRow, type TranscriptSource } from '../transport/api'
import { type TerminalSocket } from '../transport/ws'
import { TerminalViewLazy as TerminalView } from '../terminal/TerminalViewLazy'
import type { ViewMode } from './viewMode'
import { Icon } from '../ui/Icon'
import { type RoomTitle } from '../chat/roomTitles'
import { useCleanSearch } from './search/useCleanSearch'
import { turnSummary, worthCollapsing } from './turns'
import { BookmarkContext, type BookmarkCtx } from './bookmarks/BookmarkButton'
import { useBookmarks } from './bookmarks/useBookmarks'
import { noteShownFile, onJump, takeJump, type Jump } from './bookmarks/jump'

/**
 * #claudeOverlay equivalent — the agent conversation ("clean view").
 *
 * This is how you talk to an agent WITHOUT the terminal: the transcript endpoint
 * returns the parsed conversation, and /agent/message delivers into its session. The
 * terminal view (xterm + /ws) is still Phase 2; this is not a stand-in for it, it is
 * the reading/chatting surface the original also offers via the ✨ toggle.
 *
 * Tool calls are collapsed by default. In a coding-agent transcript they outnumber the
 * prose roughly 4:1, so showing them expanded buries the conversation.
 */


/**
 * Reasoning, kept but never quoted.
 *
 * A `thinking` step fell straight through to the assistant branch — the agent's
 * face, its name, and the badge "agent" on text it never said. That is the quiet
 * kind of wrong: it renders, it reads plausibly, and the only way to catch it is to
 * compare the wire against the screen. Every source can produce one; see the note on
 * TranscriptBlock for why a live count said otherwise.
 *
 * Collapsed by default and visually secondary, for the same reason the tool run is:
 * deleting it would leave a gap where the agent evidently did something, and showing
 * it inline would bury the conversation it is reasoning about.
 */
/** Start fetching before the top is reached, so the page is usually already there. */
const BACKFILL_TRIGGER_PX = 400
/** Turns drawn when a conversation opens, then how many more each frame after (the reveal in AgentPanel). */
const FIRST_TURNS = 60
const REVEAL_STEP = 60
const ALL_TURNS = Number.MAX_SAFE_INTEGER

/**
 * One line for a whole RUN of tool calls, not one bar each.
 *
 * Measured on a real transcript: 257 of 339 blocks are tool_use/tool_result — 76%
 * of the view — and they arrive in runs of up to 16 consecutive. Collapsed
 * individually they were still sixteen bars between two sentences, which is what
 * the owner meant by the repetition being meaningless. The individual collapse was
 * solving the wrong half: the depth, not the count.
 *
 * Not deleted, because a silent gap where sixteen commands ran is its own kind of
 * lie — the conversation would read as if the agent said two things in a row and
 * did nothing between them. One quiet line keeps the fact and drops the noise, and
 * opening it still gives every call in full.
 */
/** Last path segment — the full path is the title, the basename is what is scannable. */
export function AgentPanel({
  agent,
  mode,
  socketRef,
  onLink,
  onTerminalEnded,
  asking,
  onPeek,
  localMachine,
  ownerName,
  fleet,
  rooms,
  working,
}: {
  agent: AgentRow
  mode: ViewMode
  /** Shared with App so the composer can write into this agent's pty. */
  socketRef: { current: TerminalSocket | null }
  onLink?: ((state: 'connecting' | 'attached' | 'closed') => void) | undefined
  /** A plain terminal (＋ → Terminal): its session ended — `exit`, or closed elsewhere. */
  onTerminalEnded?: (() => void) | undefined
  /** The agent is stopped at a prompt this view cannot show. Badges the peek button. */
  asking?: boolean
  /**
   * The agent is BUSY (the pushed presence switch, read in App via presenceFor — never
   * the fetched row's status). While it works with nothing said yet, the in-flight
   * turn is drawn under the agent's own head rather than as a bare "show the working"
   * row (owner, 2026-09-12: 就算只有 Show the working 也要放一個 Agent 的頭像在上面).
   */
  working?: boolean
  /** Open the answer sheet. Absent in terminal-view, where the pane is already here. */
  onPeek?: (() => void) | undefined
  /** The account's name for THIS machine, for resolving a local agent's "@ server". */
  localMachine?: string | null
  /** The signed-in account — shown beside a resolved sender, per the owner's format. */
  ownerName?: string | null
  /**
   * The FLEET roster — every paired machine's agents, from getAgentsEverywhere.
   * The idle roster below covers only THIS machine, and the first live test of hash
   * resolution failed on exactly that: test1 lives on the MacBook, was absent from
   * aowl's idle list, and rendered as a bare hash. Cross-machine senders are the
   * common case for agent messages, not the edge case.
   */
  fleet?: { session: string; hash?: string; device_name?: string; host?: string | null }[] | null
  /**
   * The rooms this VIEWER can name — the projects and DMs they are a member of, as
   * {id, title}. Passed as data rather than as a resolver so the memo-stability rule
   * lives in one place (see describeRoom below), and so this component keeps doing no
   * fetching of its own.
   */
  rooms?: RoomTitle[] | null
}) {
  // terminal-view owns its own pty (it needs the byte stream to render). The
  // transcript views read over HTTP but still need a pty for the owner's input, so
  // they mount a headless socket below.
  if (mode === 'terminal-view') {
    return (
      <div id="term">
        <TerminalView
          /*
           * KEYED BY THE CONNECTION IT SHOWS, so switching agents REMOUNTS it
           * rather than re-running one effect inside a surviving instance.
           *
           * Belt to useTerminalConnection's braces (owner, 2026-09-18: the
           * Paused overlay stuck over every agent's terminal until he restarted
           * the Mac app). That effect now clears its own state, which fixes the
           * reported bug on its own — this makes the whole CLASS impossible:
           * with a fresh instance per connection there is no component state
           * left over from the previous agent for a new closure to disagree
           * with.
           *
           * The cost is a remount per agent switch, which is what a different
           * terminal on a different machine already is.
           */
          key={`${agent.device_id ?? ''}:${agent.host ?? ''}:${agent.session}`}
          session={agent.session}
          host={agent.host}
          // The agent's OWN machine, never the ambient relay target: two panes are
          // two conversations on two machines sharing one origin (owner, 2026-08-28).
          device={agent.device_id ?? null}
          socketRef={socketRef}
          onLink={onLink}
          onEnded={onTerminalEnded}
        />
      </div>
    )
  }
  // The three rich views are ONE component with a different transcript source — the
  // backend's watcher is shared the same way ("BOTH claude-view and cursor-view share
  // this one pusher", websocket.py).
  const source: TranscriptSource =
    mode === 'cursor-view'
      ? 'cursor'
      : mode === 'codex-view'
        ? 'codex'
        : mode === 'agy-view'
          ? 'agy'
          : mode === 'grok-view'
            ? 'grok'
            : mode === 'hermes-view'
              ? 'hermes'
              : mode === 'muse-view'
                ? 'muse'
                : 'claude'
  return (
    <TranscriptView
      agent={agent}
      source={source}
      socketRef={socketRef}
      onLink={onLink}
      {...(asking ? { asking } : {})}
      {...(onPeek ? { onPeek } : {})}
      {...(working ? { working } : {})}
    localMachine={localMachine ?? null}
      ownerName={ownerName ?? null}
    fleet={fleet ?? null}
      rooms={rooms ?? null}
    />
  )
}

function TranscriptView({
  agent,
  source,
  socketRef,
  onLink,
  rooms,
  asking,
  onPeek,
  localMachine,
  ownerName,
  fleet,
  working,
}: {
  agent: AgentRow
  source: TranscriptSource
  socketRef: { current: TerminalSocket | null }
  onLink?: ((state: 'connecting' | 'attached' | 'closed') => void) | undefined
  asking?: boolean
  onPeek?: (() => void) | undefined
  working?: boolean
  localMachine?: string | null
  ownerName?: string | null
  fleet?: { session: string; hash?: string; device_name?: string; host?: string | null }[] | null
  rooms?: RoomTitle[] | null
}) {
  const { resolveLabel, describeHash, describeRoom, resolutionKey } = useSenderResolution({
    localMachine,
    ownerName,
    fleet,
    rooms,
  })
  /*
   * The whole transcript window subsystem — the data window, its cache, scroll-back,
   * the event-driven re-read and the pty feed — lives in useTranscriptWindow (M8 cut
   * 3), which composes usePaneWatch and useSelectionHold. What is left here is sender
   * resolution and the render.
   */
  const pulse = useAgentPulse(agent, working)
  const { blocks, meta, loading, error, backfill, reach, log, stick, lastAssistantText, lastUserText, held, refresh, file } =
    useTranscriptWindow({
    agent,
    source,
    socketRef,
    onLink,
    pulse,
  })
  /*
   * SEARCH — owner, 2026-09-11: the composer's keyword against the blocks this view
   * holds. `items` is the grouping the render below always used; the hook memoises it
   * so the hit indices name the same items the JSX draws (agents/search/useCleanSearch).
   */
  const { items, hits, currentItem } = useCleanSearch(blocks, log, stick, {
    // The agent's own machine and hash: the search reads the WHOLE conversation there
    // (owner, 2026-09-28) and loads back to a match above what is on screen.
    device: agent.device_id ?? null,
    hash: agent.hash ?? null,
    from: meta.from,
    reach,
  })
  /*
   * THE TAIL WHILE THE AGENT IS STILL WORKING — owner, 2026-09-12: 在 Agent 還在思考 /
   * 修改代碼的時候, Show the working 的位置放錯了. 應該要放在 Agent 頭像的下面. A turn
   * with steps but no conclusion yet is the in-flight one; drawn on its own it is a
   * silent row ("no reply — N steps only") ABOVE the live block, which put the fold
   * over the avatar instead of under it. The live block takes that turn: it draws the
   * fold under its own head while the text streams, and draws the silent row itself
   * when it has no text — so exactly one of the two ever shows, decided in one place.
   */
  const tailIdx = items.length - 1
  const tailItem = items[tailIdx]
  const tail =
    tailItem && tailItem.kind === 'turn' && worthCollapsing(tailItem.blocks) && !turnSummary(tailItem.blocks)
      ? { blocks: tailItem.blocks, hits, current: tailIdx === currentItem, working: !!working }
      : undefined

  /*
   * What each "Notify agent" notice in this window was ABOUT — the tab's title and address,
   * as the agent's own browser commands in this conversation stated them. Handed down by
   * context, to the notice rows alone (agents/browserActivity.ts).
   */
  const tabsKnown = useMemo(() => tabFacts(blocks), [blocks])
  // …and which tab its own commands worked in last, for the corner of its session (AgentTabCorner).
  const tabKey = agentKey(agent)
  const notedFor = useRef<{ key: string; blocks: typeof blocks } | null>(null)
  useEffect(() => {
    const theirs = windowIsTheirs(notedFor.current, tabKey, blocks)
    notedFor.current = { key: tabKey, blocks }
    if (theirs) noteLastTab(tabKey, lastTabTouched(blocks))
  }, [tabKey, blocks])

  /*
   * BOOKMARKS (owner, 2026-10-31: 每一個 Message Block 下面有一個書簽的按鈕 … 點向某一個 List 的時候, 就可以直接跳到該
   * Message Block). A block's address is its transcript file and its absolute index (meta.from + where it sits here);
   * the buttons under the messages read it, and whether it is kept, through context (bookmarks/BookmarkButton).
   */
  const bm = useBookmarks(agent)
  const fileId = file ?? ''
  noteShownFile(agent.device_id ?? null, agent.session, file)
  const addresses = useMemo(() => new Map(blocks.map((b, i) => [b, meta.from + i])), [blocks, meta.from])
  // Read by the buttons at THEIR render: a block appended this render is placed by this render's map.
  const addrRef = useRef(addresses)
  addrRef.current = addresses
  const { list: bmList, supported: bmSupported, add: bmAdd, remove: bmRemove } = bm
  const kept = useMemo(
    () => new Map(bmList.filter((x) => x.file === fileId).map((x) => [`${x.file}:${x.index}`, x.id])),
    [bmList, fileId],
  )
  const bmCtx = useMemo<BookmarkCtx | null>(
    () =>
      bmSupported
        ? {
            addressOf: (b) => {
              const index = addrRef.current.get(b)
              return index === undefined ? null : { key: `${fileId}:${index}`, file: fileId, index }
            },
            keptAt: (k) => kept.get(k) ?? null,
            add: (b) => bmAdd(b),
            remove: (id) => bmRemove(id),
          }
        : null,
    [bmSupported, bmAdd, bmRemove, kept, fileId],
  )
  /*
   * A jump from the bookmarks list (bookmarks/jump.ts): a block above what is loaded is loaded back to (the search's
   * `reach`); then, drawn, the message is brought to the middle of the view and lit. One from an earlier conversation
   * of this agent, or older than the machine serves, is said so to the caller.
   */
  const [goTo, setGoTo] = useState<{ index: number; j: Jump } | null>(null)

  /*
   * THE NEWEST TURNS FIRST, THE REST JUST AFTER — owner, 2026-11-04: 功能變多了之後, 現在 frontend app / UI 有時候會變得很卡.
   * The window holds up to a thousand blocks so the scroll is already there (TAIL_BLOCKS), and opening an agent drew all
   * of them in one go: measured on app-dev, a 279-block conversation froze the page for ~0.2s, so a full window is the
   * better part of a second on a desktop and more on a phone — every time the person switches agents. Now the newest
   * FIRST_TURNS are drawn at once and the rest follow REVEAL_STEP at a time, a frame apart, each keeping the reader where
   * they are: the same history, there a moment later, never a long freeze. A search or a jump draws everything at once
   * (they look for their message in what is drawn), and so does reaching the top of what is drawn so far.
   */
  const revealFor = `${agent.hash}|${agent.device_id ?? ''}`
  const [shown, setShown] = useState<{ key: string; n: number }>({ key: revealFor, n: FIRST_TURNS })
  const shownN = shown.key === revealFor ? shown.n : FIRST_TURNS
  // A search or a jump looks for its message in what is drawn: everything is, from then on (a jump that lit a message
  // and then let the view go back to the newest turns would take that message off the screen).
  if ((hits.size > 0 || currentItem >= 0 || goTo !== null) && shownN < ALL_TURNS) setShown({ key: revealFor, n: ALL_TURNS })
  const drawAll = shownN >= items.length || hits.size > 0 || currentItem >= 0 || goTo !== null
  const drawFrom = drawAll ? 0 : items.length - shownN
  /** Draw `more` older turns now, keeping the reader on the line they were on (or at the bottom when stuck there). */
  const reveal = useCallback(
    (more: number) => {
      const el = log.current
      const fromBottom = el ? el.scrollHeight - el.scrollTop : 0
      flushSync(() => setShown((s) => ({ key: revealFor, n: Math.min(ALL_TURNS, (s.key === revealFor ? s.n : FIRST_TURNS) + more) })))
      if (el) el.scrollTop = stick.current ? el.scrollHeight : el.scrollHeight - fromBottom
    },
    [revealFor, log, stick],
  )
  useEffect(() => {
    if (drawAll) return
    const t = window.setTimeout(() => reveal(REVEAL_STEP), 16)
    return () => window.clearTimeout(t)
  }, [drawAll, shownN, reveal])
  // Ready to go somewhere once the blocks are here AND the bookmarks are (the messages' marks are what is gone to).
  const canJump = blocks.length > 0 && bmSupported
  const live = useRef({ fileId, from: meta.from, reach, canJump })
  live.current = { fileId, from: meta.from, reach, canJump }
  const takeIt = useCallback(() => {
    const now = live.current
    if (!now.canJump) return
    const j = takeJump(agent.device_id ?? null, agent.session)
    if (!j) return
    if (j.file && now.fileId && j.file !== now.fileId) {
      j.onResult?.('elsewhere')
      return
    }
    if (j.index >= now.from) {
      setGoTo({ index: j.index, j })
      return
    }
    void now.reach(j.index).then((ok) => (ok ? setGoTo({ index: j.index, j }) : j.onResult?.('unreachable')))
  }, [agent.device_id, agent.session])
  useEffect(() => onJump(takeIt), [takeIt])
  // A jump asked for before this view could go anywhere (the agent was just switched to): taken once it can.
  useEffect(() => {
    if (canJump) takeIt()
  }, [canJump, takeIt])
  useEffect(() => {
    if (!goTo) return
    const root = log.current
    if (!root) return
    // The message whose button holds that index — or, when it is inside a closed fold (an earlier reply), the turn's
    // own message after it.
    const marks = [...root.querySelectorAll<HTMLElement>('[data-bm-index]')]
    const at = (e: HTMLElement) => Number(e.dataset['bmIndex'])
    const mark = marks.find((e) => at(e) === goTo.index) ?? marks.filter((e) => at(e) >= goTo.index).sort((a, b) => at(a) - at(b))[0]
    const msg = (mark?.closest('.turn-reply, .pmsg') ?? null) as HTMLElement | null
    setGoTo(null)
    if (!msg) {
      goTo.j.onResult?.('unreachable')
      return
    }
    stick.current = false
    msg.scrollIntoView?.({ block: 'center' })
    msg.classList.remove('bm-flash')
    void msg.offsetWidth // restart the light when the same message is gone to twice
    msg.classList.add('bm-flash')
    msg.addEventListener('animationend', () => msg.classList.remove('bm-flash'), { once: true })
    goTo.j.onResult?.('shown')
  }, [goTo, blocks, log, stick])

  return (
    <BookmarkContext.Provider value={bmCtx}>
    <TabFactsContext.Provider value={tabsKnown}>
    <aside id="claudeOverlay" aria-label={`Conversation with ${agent.session}`}>
      <div
        id="claudeBody"
        role="log"
        aria-live="polite"
        ref={log}
        onScroll={(e) => {
          const el = e.currentTarget
          stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60
          // Fetch before the top is actually reached: at the top it is already too
          // late, the reader is looking at the gap while the request goes out.
          if (el.scrollTop < BACKFILL_TRIGGER_PX) {
            // What this device already holds is drawn before anything older is asked for.
            if (!drawAll) reveal(ALL_TURNS)
            else void backfill()
          }
        }}
      >
        {loading && blocks.length === 0 && <div className="pm-empty">loading transcript…</div>}
        {/* Says the history exists and is reachable. Without it a window that opens
            partway through a conversation is indistinguishable from one that has no
            more to show, and scrolling up looks like it did nothing. */}
        {/*
          * Only when there is positive reason to believe history exists: the window
          * starts above index 0, so something is below it. `from === 0` means this
          * window reaches the beginning — true whatever `pending` says, and older
          * edges do not send `pending` at all. Promising "scroll up to load" against
          * one of those is a permanent lie that looks exactly like a broken scroll.
          */}
        {blocks.length > 0 && !meta.end && meta.from > 0 && drawAll && (
          /*
           * A BUTTON, not just a label — owner, 2026-08-09: hidden conversation
           * + conversation >= 80 無法往上捲動. Collapsed turns can make the tail
           * window render shorter than the viewport, so it does not overflow and
           * there is nothing to scroll — the scroll-driven backfill can never
           * fire. Tapping loads the next page directly; scrolling up still works
           * when the content DOES overflow. Both reach the same backfill.
           */
          <button type="button" className="pm-empty pm-loadmore" onClick={() => void backfill()}>
            {meta.pending > 0 ? `…${meta.pending} earlier — ` : '…'}tap or scroll up to load
          </button>
        )}
        {error && (
          <div className="pm-empty sb-err">{error}</div>
        )}

        {/* One folded unit per TURN — everything the agent did between one message
            to it and the next.
          *
          * Tool runs alone were not enough. They already collapse, and the owner's
          * point is that what remains still reads as the agent talking to itself:
          * measured here, a single turn holds 26 assistant messages and 55 tool calls,
          * median 7 messages. The boundary is a message TO the agent — anything the
          * user did not send is the agent's own working and folds with the rest.
          *
          * Grouped at render rather than in state, so the transcript stays the
          * server's list, unedited. */}
        {items.map((item, i) =>
          i < drawFrom || (tail && i === tailIdx) ? null : item.kind === 'said' ? (
            /*
             * The resolver props were wired into the TURN branch only, so the
             * roster name appeared on collapsed summaries and never on the one
             * place it was asked for — the inbound message card itself. Built
             * 2026-08-07, tested at the Msg level, dead at this call site until
             * the owner saw hashes again (2026-08-09: Clean View 的 Agent 信息
             * 還是沒有被解析出來).
             */
            <Msg
              key={`s${addresses.get(item.block) ?? `i${i}`}`}
              b={item.block}
              session={agent.session}
              hash={agent.hash}
              resolve={resolveLabel}
              describeHash={describeHash}
              describeRoom={describeRoom}
              ownerName={ownerName ?? null}
              resolutionKey={resolutionKey}
              hit={hits.has(item.block)}
              current={i === currentItem}
            />
          ) : (
            <Turn
              // By where its first block sits in the conversation, not by its place in this window: loading older
              // history shifted every place, drew every turn again and moved each turn's open fold onto another one.
              key={`t${addresses.get(item.blocks[0]!) ?? `i${i}`}`}
              blocks={item.blocks}
              session={agent.session}
              hash={agent.hash}
              resolve={resolveLabel}
            describeHash={describeHash}
              describeRoom={describeRoom}
              ownerName={ownerName ?? null}
            resolutionKey={resolutionKey}
              hits={hits}
              current={i === currentItem}
            />
          ),
        )}
        {/*
          * The message the agent is part-way through writing, when the pane is sure
          * enough to show one. It draws through `Msg` — the same component every real
          * block uses, per the owner's choice of 跟真的 block 完全一樣的樣式 — and it
          * subscribes to the pane store itself, so a capture repaints THIS and nothing
          * above it. It is state parallel to `blocks`, never part of it.
          */}
        {/*
          * WHAT THE PERSON JUST SENT, before the transcript can say it — owner,
          * 2026-09-16: 使用者輸入的文字就全部都被吃掉了 … 當該 Agent 開始輸出之後,
          * 使用者的文字以及 Agent 本身的文字又可以被看到. An agent's composer posts no
          * message row, and claude writes its transcript when a message COMPLETES, so
          * both halves of that turn land at once and the gap before them was blank.
          * Between the settled blocks and the reply being written is where it belongs
          * in time; it removes itself the moment the real record arrives.
          */}
        <SentEcho
          session={agent.session}
          hash={agent.hash}
          lastUser={lastUserText}
          resolve={resolveLabel}
          describeHash={describeHash}
          describeRoom={describeRoom}
          ownerName={ownerName ?? null}
          resolutionKey={resolutionKey}
        />
        <LiveBlock
          lastAssistant={lastAssistantText}
          session={agent.session}
          hash={agent.hash}
          resolve={resolveLabel}
          describeHash={describeHash}
          describeRoom={describeRoom}
          ownerName={ownerName ?? null}
          resolutionKey={resolutionKey}
          tail={tail}
        />
      </div>

      {onPeek && <PeekButton {...(asking ? { asking } : {})} onPeek={onPeek} />}

      {/* The line is also the manual way out of a stuck view: a tap re-reads the
          conversation, shows anything held back and re-arms the watch (refresh). */}
      <div className="ap-foot" onClick={refresh} title="Tap to refresh">
        <Icon name="auto_awesome" size={13} />
        <span>
          {/*
            * THE SOURCE FIRST, then what is loaded. `total` is the transcript's own
            * count and holds still; `blocks.length` is this window and moves — it grows
            * with every append while the agent works and jumps by a page on every
            * scroll-back, so it changed for reasons the reader could not see. Showing
            * both also makes scroll-back legible: `loaded` climbing by a page is the
            * feedback that pressing on upward did something.
            */}
          {/*
            * THE SOURCE FIRST, then how much of it is here.
            *
            * `total` is the transcript's own count and holds still; `blocks.length` is
            * this window and moves — it grows with every append and jumps by a page on
            * every scroll-back. Printing only the second one is what made the owner ask
            * why the number kept changing, and they were right to: it answered a
            * question nobody had asked.
            *
            * `loaded` appears only while there is more to load, so a fully-read
            * conversation reads as one number rather than as two that agree.
            */}
          {source} view · {meta.total || blocks.length} blocks
          {meta.total > blocks.length ? ` · ${blocks.length} loaded` : ''}
          {agent.host ? ` · ${agent.host}` : ''}
          {/*
            * WHY IT IS NOT MOVING, when it is not (owner, 2026-10-04): new blocks are
            * waiting behind a text selection. Said here, where the count already is, and
            * the whole line is the way out — tap it to show them.
            */}
          {held ? ' · paused while selecting — tap to show new' : ''}
        </span>
      </div>
    </aside>
    </TabFactsContext.Provider>
    </BookmarkContext.Provider>
  )
}

/**
 * Internals reachable by tests only.
 *
 * `ToolRun` is not part of this module's interface — App renders `AgentPanel`. Naming
 * the escape hatch rather than exporting the component keeps that distinction: a
 * future import of ToolRun elsewhere reads as a mistake, which a plain export would
 * not.
 */
/*
 * TranscriptView is here for the windowing tests. The scroll-back bug the owner hit —
 * a backfill landing on a window that had already been reset — is a property of THIS
 * component's refs, and every lighter mount that renders its pieces would have stayed
 * green through it. peekButton.test.tsx already records the cost of testing one level
 * too low; this is the same lesson, paid once.
 */
export const AgentPanelTestables = { ToolRun, PeekButton, Turn, TranscriptView }
